import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { css } from "styled-components";

/* Store Custom Code - Start */
import { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { SPACING } from "@store/theme/common/design";
import { contentWidthStyles } from "@store/theme/common/mixins";
import breakpointValuesMediaQueries from "@store/Framework/Common/Utilities/breakpointValuesMediaQueries";
/* Store Custom Code - End */

interface OwnProps extends WidgetProps {
    extendedStyles?: FooterContainerStyles;
}

export interface FooterContainerStyles {
    widgetGridContainer?: GridContainerProps;
    logoGridItem?: GridItemProps;
    linkList1GridItem?: GridItemProps;
    linkList2GridItem?: GridItemProps;
    linkList3GridItem?: GridItemProps;
    linkList4GridItem?: GridItemProps;
    subscribeGridItem?: GridItemProps;
    socialLinksGridItem?: GridItemProps;
    siteMapLinksGridItem?: GridItemProps;
    richContentGridItem?: GridItemProps;
    logoWrapper?: InjectableCss;
    // Store Custom Code
    wrapper?: InjectableCss;
    footerLogoGridItem?: GridItemProps;
    footerSeparatorGridItem?: GridItemProps;
    footerSeparator?: InjectableCss;
    footerDetailsGridItem?: GridItemProps;
    footerLinksGridItem?: GridItemProps;
    linkListGridItem?: GridItemProps;
    footerLegalGridContainer?: GridContainerProps;
    footerLegalGridItem?: GridItemProps;
    footerInfoGridItem?: GridItemProps;
}

export const footerContainerStyles: FooterContainerStyles = {
    logoGridItem: {
        width: 12,
        css: css`
            width: 100%;
            display: flex;
            justify-content: center;
            img {
                height: 78px;
            }
        `,
    },
    linkList1GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    linkList2GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    linkList3GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    linkList4GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    subscribeGridItem: {
        width: [12, 12, 8, 4, 4],
    },
    socialLinksGridItem: {
        width: 12,
    },
    siteMapLinksGridItem: {
        width: 12,
    },
    richContentGridItem: {
        width: 12,
    },
    logoWrapper: {
        css: css`
            width: 110px;
        `,
    },
    // Store Custom Code
    widgetGridContainer: {
        gap: 40,
        css: css`
            ${contentWidthStyles()}
            padding: 20px; /* fixed px to fit Grid gap */
            background-color: ${getColor("secondary")};
        `,
    },
    wrapper: {
        css: css`
            ${contentWidthStyles()}
            width: 100%;
            display: flex;
            flex-wrap: inherit;
        `,
    },
    footerDetailsGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            flex-wrap: inherit;
        `,
    },
    footerLogoGridItem: {
        css: css`
            width: auto;
            height: inherit;
            flex-basis: auto;
            align-items: center;
            max-width: inherit;
            min-width: 120px;
            padding: ${SPACING.medium} 0;

            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [{ values: ["inherit", null, "100%", null, null], properties: ["height"] }],
                    mediaOptions.min,
                )}
        `,
    },
    footerSeparatorGridItem: {
        css: css`
            display: none;
            flex-basis: auto;
            width: 85px;
            max-width: inherit;
            position: relative;
            padding: ${SPACING.medium} 0;
            height: 100%;

            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [{ values: ["none", null, "block", null, null], properties: ["display"] }],
                    mediaOptions.min,
                )}
        `,
    },
    footerSeparator: {
        css: css`
            position: absolute;
            width: 0;
            height: 100%;
            top: 0;
            left: 50%;
            transform: translateX(-50%);
            background-color: ${getColor("common.borderDark")};

            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [{ values: [0, null, "1px", null, null], properties: ["width"] }],
                    mediaOptions.min,
                )}
        `,
    },
    footerInfoGridItem: {
        width: [12, 12, 5, 5, 5],
        css: css`
            padding: ${SPACING.medium} 0;
            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [{ values: [0, null, `${SPACING.medium}`, null, null], properties: ["padding-bottom"] }],
                    mediaOptions.min,
                )}
        `,
    },
    footerLinksGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            flex-wrap: inherit;
        `,
    },
    linkListGridItem: {
        width: [12, 12, 4, 4, 4],
        css: css`
            padding: 0;

            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [{ values: [0, null, `${SPACING.medium} 0`, null, null], properties: ["padding"] }],
                    mediaOptions.min,
                )}

            h3 {
                margin-bottom: 5px;
                color: ${getColor("secondary.contrast")} !important; /* forcing white on footer headers */
                text-transform: uppercase;
            }

            a {
                margin-bottom: 5px;
            }

            a,
            span {
                color: ${getColor("text.lighter")} !important; /* Store Custom Code - forcing grey on footer links */
                text-transform: none;
            }
        `,
    },
    footerLegalGridContainer: {
        gap: 40,
        css: css`
            padding: 20px; /* Store Custom Code - fixed px to fit Grid gap */
            background-color: ${getColor("user.guest")};
        `,
    },
    footerLegalGridItem: {
        width: 12,
        align: "middle",
        css: css`
            p {
                margin-bottom: 0;
                padding: 0;
                opacity: 0.7;
            }
        `,
    },
};

const FooterContainer: FC<OwnProps> = ({ id, extendedStyles }) => {
    const [styles] = React.useState(() => mergeToNew(footerContainerStyles, extendedStyles));
    /* Store Custom Code - Start */
    return (
        <div>
            <GridContainer {...styles.widgetGridContainer}>
                <StyledWrapper {...styles.wrapper}>
                    <GridItem {...styles.footerDetailsGridItem}>
                        <GridItem {...styles.footerLogoGridItem}>
                            <Zone zoneName="FooterLogo" contentId={id} />
                        </GridItem>
                        <GridItem {...styles.footerSeparatorGridItem}>
                            <StyledWrapper {...styles.footerSeparator}></StyledWrapper>
                        </GridItem>
                        <GridItem {...styles.footerInfoGridItem}>
                            <Zone zoneName="FooterInfo" contentId={id} />
                        </GridItem>
                    </GridItem>
                    <GridItem {...styles.footerLinksGridItem}>
                        <GridItem {...styles.linkListGridItem}>
                            <Zone zoneName="LinkList1" contentId={id} />
                        </GridItem>
                        <GridItem {...styles.linkListGridItem}>
                            <Zone zoneName="LinkList2" contentId={id} />
                        </GridItem>
                        <GridItem {...styles.linkListGridItem}>
                            <Zone zoneName="LinkList3" contentId={id} />
                        </GridItem>
                    </GridItem>
                </StyledWrapper>
            </GridContainer>
            <GridContainer {...styles.footerLegalGridContainer}>
                <StyledWrapper {...styles.wrapper}>
                    <GridItem {...styles.footerLegalGridItem}>
                        <Zone zoneName="FooterLegal" contentId={id} />
                    </GridItem>
                </StyledWrapper>
            </GridContainer>
        </div>
    );
    /* Store Custom Code - End */
};

const footerContainer: WidgetModule = {
    component: FooterContainer,
    definition: {
        group: "Footer",
        allowedContexts: ["Footer"],
        isSystem: true, // Store Custom Code
    },
};

export default footerContainer;
